import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  alpha,
} from '@mui/material';
import { DateRange } from '@mui/icons-material';
import { Community } from '../../types/community';

interface AboutTabProps {
  community: Community;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  glassEffect: any;
}

const AboutTab: React.FC<AboutTabProps> = ({
  community,
  isDark,
  goldColor,
  deepGreen,
  glassEffect,
}) => {
  return (
    <Card sx={{ 
      p: { xs: 2, md: 4 },
      borderRadius: '20px',
      ...glassEffect,
      border: `1px solid ${alpha(goldColor, 0.2)}`,
      minHeight: { xs: 'auto', md: '400px' }
    }}>
      <Typography variant="h5" sx={{ 
        mb: 3, 
        fontWeight: 800, 
        color: isDark ? '#fff' : deepGreen,
        fontSize: { xs: '1.2rem', md: '1.5rem' }
      }}>
        About Community
      </Typography>
      
      <Typography sx={{ 
        mb: 4, 
        lineHeight: 1.6, 
        whiteSpace: 'pre-wrap', 
        color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
        fontSize: { xs: '0.9rem', md: '1rem' }
      }}>
        {community.description}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ 
          mb: 2, 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          color: isDark ? '#fff' : deepGreen,
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}>
          <DateRange sx={{ color: goldColor, fontSize: { xs: '0.9rem', md: '1rem' } }} />
          Created
        </Typography>
        <Typography sx={{ 
          color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
          fontSize: { xs: '0.85rem', md: '1rem' }
        }}>
          {new Date(community.createdAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ 
          mb: 2, 
          fontWeight: 700, 
          color: isDark ? '#fff' : deepGreen,
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}>
          Category
        </Typography>
        <Chip
          label={community.category}
          sx={{
            bgcolor: alpha(goldColor, 0.1),
            color: goldColor,
            fontWeight: 600,
            fontSize: { xs: '0.8rem', md: '0.9rem' },
            px: { xs: 0.5, md: 1 },
            py: { xs: 0.5, md: 1 }
          }}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ 
          mb: 2, 
          fontWeight: 700, 
          color: isDark ? '#fff' : deepGreen,
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}>
          Tags
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {community.tags && community.tags.length > 0 ? (
            community.tags.map((tag: string) => (
              <Chip
                key={tag}
                label={`#${tag}`}
                size="small"
                sx={{
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  '&:hover': {
                    bgcolor: alpha(goldColor, 0.2)
                  }
                }}
              />
            ))
          ) : (
            <Typography variant="body2" sx={{ 
              color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              fontSize: { xs: '0.85rem', md: '1rem' }
            }}>
              No tags added
            </Typography>
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ 
          mb: 2, 
          fontWeight: 700, 
          color: isDark ? '#fff' : deepGreen,
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}>
          Community Stats
        </Typography>
        <Grid container spacing={{ xs: 1, md: 2 }}>
          <Grid size={{ xs: 6 }}>
            <Card sx={{ 
              bgcolor: isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05),
              border: `1px solid ${alpha(goldColor, 0.2)}`,
              borderRadius: '16px',
              ...glassEffect,
              height: '100%',
              minWidth: '120px'
            }}>
              <CardContent sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
                <Typography variant="h3" sx={{ 
                  color: goldColor, 
                  fontWeight: 800, 
                  mb: 1,
                  fontSize: { xs: '1.8rem', md: '3rem' }
                }}>
                  {community.membersCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', 
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', md: '1rem' }
                }}>
                  Members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Card sx={{ 
              bgcolor: isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05),
              border: `1px solid ${alpha(goldColor, 0.2)}`,
              borderRadius: '16px',
              ...glassEffect,
              height: '100%',
              minWidth: '120px'
            }}>
              <CardContent sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
                <Typography variant="h3" sx={{ 
                  color: goldColor, 
                  fontWeight: 800, 
                  mb: 1,
                  fontSize: { xs: '1.8rem', md: '3rem' }
                }}>
                  {community.postsCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', 
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', md: '1rem' }
                }}>
                  Posts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default AboutTab;